import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styles from './ReportButtons.module.sass'
import RequestAccessModal from 'src/features/paywall/components/RequestModal'
import UpcomingAndAuthenticated from './UpcomingAndAuthenticated'
import UpcomingAndAnonymous from './UpcomingAndAnonymous'
import HasAccess from './HasAccess'
import DoesNotHaveAccess from './DoesNotHaveAccess'
import AnonymousAndNotUpcoming from './AnonymousAndNotUpcoming'
import usePiwik from 'src/hooks/usePiwik'
import EmailConfirmation from 'src/features/paywall/components/EmailConfirmation'

const ReportButtons = ({
  articleId,
  user = null,
  authenticated = false,
  reportSettings = {}
}) => {
  const { handleTrackingWithArticleIdProp } = usePiwik()
  const [modalVisible, setModalVisible] = useState(false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [modalType, setModalType] = useState('')
  const [disableConsultantContact, setDisableConsultantContact] =
    useState(false)
  const [notifySettings, setNotifySettings] = useState(null)
  const upcoming = reportSettings?.upcoming ?? false
  const hasAccess = reportSettings?.hasAccess ?? false

  const openModal = () => {
    setModalVisible(true)
  }

  const closeModal = () => {
    handleTrackingWithArticleIdProp(
      modalType === 'notify-me'
        ? 'pop-up'
        : 'pop-up_contact-me-about-this-report',
      'click',
      'close-window',
      articleId
    )
    setModalVisible(false)
  }

  const openModalWithTracking = (
    trackingType,
    disableConsultantContact,
    modalType = 'contact-me'
  ) => {
    const trackingOptions = {
      'contact-me-about-this-report': {
        category: 'pop-up_contact-me-about-this-report',
        action: 'view',
        actionType: 'contact-me-about-this-report'
      },
      'notify-me': {
        category: 'pop-up',
        action: 'view',
        actionType: 'notify-me-anon_form'
      }
    }

    if (trackingType in trackingOptions) {
      const { category, action, actionType } = trackingOptions[trackingType]
      handleTrackingWithArticleIdProp(category, action, actionType, articleId)
    }
    setModalType(modalType)
    setDisableConsultantContact(disableConsultantContact)
    openModal()
  }

  const withSubmitTracking = () => {
    handleTrackingWithArticleIdProp(
      modalType === 'notify-me'
        ? 'pop-up'
        : 'pop-up_contact-me-about-this-report',
      'button-click',
      modalType === 'notify-me' ? 'notify-me-anon_form_submit' : 'submit',
      articleId
    )
  }

  const renderButtons = () => {
    // Scenario 1
    if (upcoming && authenticated) {
      return (
        <div className={styles.upcomingAndAuthenticated}>
          <UpcomingAndAuthenticated
            contactAction={openModalWithTracking}
            articleId={articleId}
            user={user}
            {...reportSettings}
          />
        </div>
      )
    }
    // Scenario 2
    if (upcoming && !authenticated)
      return (
        <UpcomingAndAnonymous
          openModal={openModalWithTracking}
          articleId={articleId}
          setNotifySettings={setNotifySettings}
          productCode={reportSettings?.productCode}
        />
      )
    // Scenario 3
    if (!upcoming && authenticated && hasAccess)
      return <HasAccess articleId={articleId} reportSettings={reportSettings} />
    // Scenario 4
    if (!upcoming && authenticated && !hasAccess)
      return (
        <DoesNotHaveAccess
          openModal={openModalWithTracking}
          articleId={articleId}
        />
      )
    // Scenario 5
    if (!upcoming && !authenticated)
      return (
        <AnonymousAndNotUpcoming
          openModal={openModalWithTracking}
          articleId={articleId}
        />
      )
  }

  const handleSubmitForm = () => {
    setModalVisible(false)
    setIsConfirmationModalOpen(true)
  }

  return (
    <>
      {renderButtons()}
      <RequestAccessModal
        open={modalVisible}
        onCancel={closeModal}
        onSuccess={handleSubmitForm}
        disableConsultantContact={disableConsultantContact}
        withSubmitTracking={withSubmitTracking}
        notifySettings={notifySettings}
      />
      <EmailConfirmation
        open={isConfirmationModalOpen}
        onCancel={() => setIsConfirmationModalOpen(false)}
      />
    </>
  )
}

ReportButtons.propTypes = {
  user: PropTypes.object,
  authenticated: PropTypes.bool,
  articleId: PropTypes.any,
  reportSettings: PropTypes.object
}

export default ReportButtons
